import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HEAD_DOCTOR_PATHS } from '../../doctor/doctorPaths';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export function countDaysInclusive(from, to) {
  if (!from || !to) return 1;
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  const diff = Math.floor((b.getTime() - a.getTime()) / 86400000);
  return Math.max(1, diff + 1);
}

export function lineCount(byLine, serviceLine) {
  return Number((byLine || []).find((r) => r.service_line === serviceLine)?.count || 0);
}

export function statusCount(byStatus, status) {
  return Number(
    (byStatus || []).find((r) => String(r.status || '').toUpperCase() === String(status).toUpperCase())
      ?.count || 0
  );
}

export function fmtSynthesisNumber(n) {
  return Number(n || 0).toLocaleString('fr-FR');
}

export function fmtSynthesisAvg(total, days) {
  const avg = Number(total || 0) / Math.max(1, days);
  return avg.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function nursingActsTotal(summary = {}) {
  return (
    Number(summary.nursing_care_actions || 0) +
    Number(summary.nursing_injection_actions || 0) +
    Number(summary.nursing_test_results || 0) +
    Number(summary.nursing_vaccinations || 0) +
    Number(summary.nursing_family_planning_actions || 0)
  );
}

/**
 * Groupes du tableau consolidé (indicateurs / total / moy. jour).
 * @param {object} summary
 * @param {Array} byLine consultations.by_service_line
 * @param {number} days
 * @param {object} [paths] override des liens (défaut HEAD_DOCTOR_PATHS)
 */
export function buildSynthesisRows(summary = {}, byLine = [], days = 1, paths = HEAD_DOCTOR_PATHS) {
  const medical = lineCount(byLine, 'MEDICAL');
  const dental = lineCount(byLine, 'DENTAL');
  const nursingLine = lineCount(byLine, 'NURSING');
  const actsTotal = nursingActsTotal(summary);

  return [
    {
      section: COPY.SYNTHESE_TABLE_SEC_VISITS,
      tone: 'indigo',
      rows: [
        {
          key: 'consultations',
          label: COPY.SYNTHESE_ROW_VISITS,
          total: summary.consultations,
          link: paths.STATISTIQUES_CONSULTATIONS
        },
        {
          key: 'medical',
          label: COPY.SYNTHESE_ROW_MEDICAL,
          total: medical,
          indent: true,
          link: paths.STATISTIQUES_CONSULTATIONS
        },
        {
          key: 'dental',
          label: COPY.SYNTHESE_ROW_DENTAL,
          total: dental,
          indent: true,
          link: paths.STATISTIQUES_CONSULTATIONS
        },
        {
          key: 'nursing_line',
          label: COPY.SYNTHESE_ROW_NURSING_LINE,
          total: nursingLine,
          indent: true,
          link: paths.STATISTIQUES_CONSULTATIONS
        }
      ]
    },
    {
      section: COPY.SYNTHESE_TABLE_SEC_RISKS,
      tone: 'amber',
      rows: [
        {
          key: 'at',
          label: COPY.SYNTHESE_ROW_AT,
          total: summary.work_accidents,
          link: paths.STATISTIQUES_WORK_ACCIDENTS
        },
        {
          key: 'mp',
          label: COPY.SYNTHESE_ROW_MP,
          total: summary.occupational_diseases,
          link: paths.STATISTIQUES_OCCUPATIONAL_DISEASES
        }
      ]
    },
    {
      section: COPY.SYNTHESE_TABLE_SEC_NURSING,
      tone: 'teal',
      rows: [
        {
          key: 'nursing_visits',
          label: COPY.SYNTHESE_ROW_NURSING_VISITS,
          total: summary.nursing_visits,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'nursing_acts',
          label: COPY.SYNTHESE_ROW_NURSING_ACTS,
          total: actsTotal,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'care',
          label: COPY.SYNTHESE_ROW_CARE,
          total: summary.nursing_care_actions,
          indent: true,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'injections',
          label: COPY.SYNTHESE_ROW_INJECTIONS,
          total: summary.nursing_injection_actions,
          indent: true,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'tests',
          label: COPY.SYNTHESE_ROW_TESTS,
          total: summary.nursing_test_results,
          indent: true,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'vaccines',
          label: COPY.SYNTHESE_ROW_VACCINES,
          total: summary.nursing_vaccinations,
          indent: true,
          link: paths.STATISTIQUES_SOINS
        },
        {
          key: 'fp',
          label: COPY.SYNTHESE_ROW_FP,
          total: summary.nursing_family_planning_actions,
          indent: true,
          link: paths.STATISTIQUES_SOINS
        }
      ]
    },
    {
      section: COPY.SYNTHESE_TABLE_SEC_CLINICAL,
      tone: 'violet',
      rows: [
        {
          key: 'exams',
          label: COPY.SYNTHESE_ROW_EXAMS,
          total: summary.exam_orders,
          link: paths.STATISTIQUES_EXAMENS
        },
        {
          key: 'patho',
          label: COPY.SYNTHESE_ROW_PATHOLOGIES,
          total: summary.unique_pathologies,
          link: paths.STATISTIQUES_PATHOLOGIES
        },
        {
          key: 'patho_cases',
          label: COPY.SYNTHESE_ROW_PATHO_CASES,
          total: summary.pathology_occurrences,
          indent: true,
          link: paths.STATISTIQUES_PATHOLOGIES
        },
        {
          key: 'rx',
          label: COPY.SYNTHESE_ROW_RX,
          total: summary.prescriptions,
          link: paths.STATISTIQUES_ORDONNANCES
        },
        {
          key: 'meds',
          label: COPY.SYNTHESE_ROW_MEDS,
          total: summary.unique_medications,
          link: paths.STATISTIQUES_MEDICAMENTS
        }
      ]
    },
    {
      section: COPY.SYNTHESE_TABLE_SEC_REST,
      tone: 'slate',
      rows: [
        {
          key: 'rest_cert',
          label: COPY.SYNTHESE_ROW_REST_CERT,
          total: summary.rest_day_certificates,
          link: paths.STATISTIQUES_JOURS_REPOS
        },
        {
          key: 'rest_days',
          label: COPY.SYNTHESE_ROW_REST_DAYS,
          total: summary.rest_day_total_days,
          indent: true,
          link: paths.STATISTIQUES_JOURS_REPOS
        },
        {
          key: 'rest_people',
          label: COPY.SYNTHESE_ROW_REST_PEOPLE,
          total: summary.rest_day_unique_persons,
          indent: true,
          link: paths.STATISTIQUES_JOURS_REPOS
        }
      ]
    }
  ].map((group) => ({
    ...group,
    rows: group.rows.map((row) => ({
      ...row,
      avg: fmtSynthesisAvg(row.total, days)
    }))
  }));
}
